/**
 * Sign in.
 *
 * Only the providers this deployment can actually finish a sign-in with are
 * offered — `/api/session` reports them — so nobody is sent to an authorize
 * URL that fails on the way back. `?next=` is carried through to
 * `/auth/:provider/start`, which puts it inside the state cookie; that is what
 * returns someone to the link or consent request they started.
 */

import type { ReactNode } from "react";
import { useEffect } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Container from "react-bootstrap/Container";
import { useSearchParams } from "react-router";
import type { OAuthProviderId } from "@xtract-bot/embellisher-schema";
import { SkeletonRows } from "../components/ui";
import { useSession } from "../useSession";

const PROVIDERS: Record<OAuthProviderId, { name: string; icon: ReactNode }> = {
  github: {
    name: "GitHub",
    icon: (
      <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27s1.36.09 2 .27c1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8Z" />
      </svg>
    ),
  },
  gitlab: {
    name: "GitLab",
    icon: (
      <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="m15.73 6.52-.02-.06-2.17-5.66a.57.57 0 0 0-.55-.35.56.56 0 0 0-.53.4L11 5.51H5L3.54.85a.56.56 0 0 0-.53-.4.57.57 0 0 0-.55.36L.29 6.47l-.02.05a4.03 4.03 0 0 0 1.34 4.65l.01.01 3.6 2.7 1.78 1.35 1.09.82a.66.66 0 0 0 .8 0l1.09-.82 1.78-1.35 3.62-2.71a4.03 4.03 0 0 0 1.35-4.65Z" />
      </svg>
    ),
  },
  bitbucket: {
    name: "Bitbucket",
    icon: (
      <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M.78 1.3a.63.63 0 0 0-.63.73l2.14 13a.86.86 0 0 0 .84.71h10.3a.63.63 0 0 0 .63-.53l2.14-13.18a.63.63 0 0 0-.63-.73H.78ZM9.7 10.5H6.34L5.43 5.7h5.09L9.7 10.5Z" />
      </svg>
    ),
  },
};

function safeNext(raw: string | null): string {
  // The Worker applies the same rule server-side; matching it here means the
  // button does not silently send someone somewhere else than it says.
  if (!raw) return "";
  if (!raw.startsWith("/") || raw.startsWith("//")) return "";
  if (raw.includes("\\") || raw.includes("://")) return "";
  return raw;
}

export function SignIn(): ReactNode {
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const { account, providers, loading, error } = useSession();

  useEffect(() => {
    // Already signed in and sent here on the way to somewhere: finish the trip.
    if (!loading && account && next) window.location.replace(next);
  }, [loading, account, next]);

  return (
    <Container style={{ maxWidth: "31rem" }}>
      <Card>
        <Card.Body className="p-4 p-md-5">
          <h1 className="h4 mb-1">Sign in</h1>
          <p className="emb-muted mb-4">
            {next === "/install"
              ? "Sign in to download the extension. If you were invited, use the provider account whose verified email address is the one the invitation went to — any other address is refused."
              : next
                ? "Sign in to continue — you will come straight back here."
                : "Sign in to link a browser and connect your assistant."}
          </p>

          {loading ? (
            <SkeletonRows rows={3} />
          ) : error ? (
            <Alert variant="danger" className="mb-0">
              {error}
            </Alert>
          ) : account ? (
            <Alert variant="success" className="mb-0">
              You are signed in as <strong>{account.username}</strong>.{" "}
              <Alert.Link href={next || "/account"}>{next ? "Continue" : "Go to your account"}</Alert.Link>
            </Alert>
          ) : providers.length === 0 ? (
            <Alert variant="warning" className="mb-0">
              <p className="fw-semibold mb-1">No sign-in provider is configured on this deployment.</p>
              <p className="mb-0 small">
                Signing in needs a client id and secret for at least one of GitHub, GitLab or Bitbucket, set with{" "}
                <code>wrangler secret put</code>. See <code>docs/AUTH.md</code> and the worker README.
              </p>
            </Alert>
          ) : (
            <div className="d-grid gap-2">
              {providers.map((id) => (
                <Button
                  key={id}
                  href={`/auth/${id}/start${next ? `?next=${encodeURIComponent(next)}` : ""}`}
                  variant="outline-primary"
                  size="lg"
                  className="d-flex align-items-center justify-content-center gap-2"
                >
                  {PROVIDERS[id].icon}
                  Continue with {PROVIDERS[id].name}
                </Button>
              ))}
            </div>
          )}

          <hr className="my-4" />
          <p className="emb-muted small mb-0">
            Identity only: no repository, no code, no organisation access is requested. Your verified email is used to
            decide whether you are an administrator of this deployment, and for nothing else.
          </p>
        </Card.Body>
      </Card>
    </Container>
  );
}
